import useScrollReveal from '../hooks/useScrollReveal';
import { SERVICES_DATA, TIMETABLE } from '../data';
import './Services.css';

export default function Services() {
  const ref = useScrollReveal();

  const processSteps = [
    {
      icon: '🚪',
      title: 'Join & Assess',
      text: 'Sign up online or at reception and complete a proper fitness assessment with one of our expert coaches.',
    },
    {
      icon: '📋',
      title: 'Get Your Plan',
      text: 'We create a personalised training and nutrition plan based on your body type, goals, and fitness level.',
    },
    {
      icon: '💪',
      title: 'Train Hard',
      text: 'Start your sessions with full access to the gym, expert support, and a motivating environment that keeps you focused.',
    },
    {
      icon: '📈',
      title: 'Track Progress',
      text: 'Regular check-ins and progress tracking help you stay consistent, accountable, and confident in your journey.',
    },
    {
      icon: '🔄',
      title: 'Evolve & Adapt',
      text: 'Your plan is updated over time so your body keeps improving and your results never slow down.',
    },
  ];

  const perks = [
    {
      icon: '🛁',
      title: 'Hygienic Washroom Area',
      text: 'Clean changing areas, private showers, and a premium experience for every member.',
    },
    {
      icon: '📱',
      title: 'Fitness King App',
      text: 'Track workouts, follow routines, and stay connected with your training goals.',
    },
    {
      icon: '🥤',
      title: 'Nutrition Support',
      text: 'Access healthy meal guidance, supplement support, and performance-focused nutrition help.',
    },
    {
      icon: '🔒',
      title: 'Secure Access',
      text: 'Train with confidence in a safe and secure environment designed for serious fitness.',
    },
    {
      icon: '🧊',
      title: 'Recovery Zone',
      text: 'Improve recovery with supportive post-workout options that help your body recharge.',
    },
    {
      icon: '🎧',
      title: 'High-Energy Vibe',
      text: 'A motivating gym atmosphere with music and energy that keeps every workout intense.',
    },
  ];

  return (
    <div ref={ref}>
      {/* HERO */}
      <div className="page-hero">
        <div className="services-hero-bg" />
        <div className="page-hero-overlay" />
        <div className="page-hero-content">
          <div className="sec-eyebrow fade-up">Everything You Need</div>
          <h1 className="page-title-xl fade-up delay-1">
            OUR <span className="acc">SERVICES</span>
          </h1>
        </div>
      </div>

      {/* INTRO */}
      <section className="section services-intro-section">
        <div className="section-inner">
          <div className="text-center fade-up services-intro-head">
            <h2 className="sec-title">
              PROGRAMMES BUILT FOR <span className="acc">YOUR GOALS</span>
            </h2>
            <div className="divider center" />
            <p className="sec-desc services-intro-desc">
              Whether you are starting your fitness journey or pushing for your next
              level, Fitness King offers the right coaching, training, and environment
              to help you improve faster.
            </p>
          </div>
        </div>

        <div className="service-grid services-grid-wrap">
          {SERVICES_DATA.map((s, i) => (
            <div
              className={`svc-card fade-up delay-${(i % 3) + 1}`}
              key={s.title}
            >
              <div
                className="svc-bg"
                style={{ backgroundImage: `url('${s.bg}')` }}
              />
              <div className="svc-gradient" />
              <div className="svc-content">
                <div className="svc-icon-wrap">{s.icon}</div>
                <div className="svc-title">{s.title}</div>
                <div className="svc-desc">{s.desc}</div>
                <div className="svc-link">
                  Book a Session <span>→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section services-process-section">
        <div className="section-inner">
          <div className="text-center fade-up services-section-head">
            <div className="sec-eyebrow center-eyebrow">The Process</div>
            <h2 className="sec-title">
              HOW IT <span className="acc">WORKS</span>
            </h2>
            <div className="divider center" />
          </div>

          <div className="process-steps">
            {processSteps.map((p, i) => (
              <div className={`ps-item fade-up delay-${(i % 3) + 1}`} key={p.title}>
                <div className="ps-icon">{p.icon}</div>
                <div className="ps-title">{p.title}</div>
                <div className="ps-text">{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMETABLE */}
      <section className="section">
        <div className="section-inner">
          <div className="fade-up services-section-head-left">
            <div className="sec-eyebrow">Weekly Schedule</div>
            <h2 className="sec-title">
              CLASS <span className="acc">TIMETABLE</span>
            </h2>
            <div className="divider" />
          </div>

          <div className="timetable-wrap fade-up delay-2">
            <table className="timetable">
              <thead>
                <tr>
                  {['Time', 'Monday', 'Wednesday', 'Friday', 'Saturday'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TIMETABLE.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={j === 0 ? 'time-cell' : ''}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="section services-perks-section">
        <div className="section-inner">
          <div className="text-center fade-up services-section-head">
            <div className="sec-eyebrow center-eyebrow">
              Included With Every Membership
            </div>
            <h2 className="sec-title">
              Fitness King <span className="acc">PERKS</span>
            </h2>
            <div className="divider center" />
          </div>

          <div className="perks-grid">
            {perks.map((p, i) => (
              <div className={`perk-card fade-up delay-${(i % 3) + 1}`} key={p.title}>
                <div className="perk-icon">{p.icon}</div>
                <div className="perk-title">{p.title}</div>
                <div className="perk-text">{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}